import { html, css } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { ContextData, ReportContext } from '@reporting/rp-types/analytics';
import { BaseVisualization, VisualizationAction, ReportEvent, ExportType, VisualizationOptions } from "./baseVisualization";
import { Layout, dom } from '@omegagrid/core';
import rp, { parseComponentId } from '@reporting/rp-app-host';
import { ModifyContextParams } from '../../types';
import { getDrillContainerId, openDrillDialog } from './drillDown';

type ExtensionEngine = {
	getCustomActions(): VisualizationAction[];
	getExportTypes(): ExportType[];
	invokeAction(id: string, data?: any): void;
	export(type: ExportType): Promise<void>;
};

type ExtensionDetailData = {
	idResource: number;
	caption: string;
	parameters: Record<string, unknown>;
};

@customElement('rp-visual-extension')
export class ExtensionVisualization extends BaseVisualization<ContextData> {

	static styles = css`
		:host {
			display: block;
			width: 100%;
			height: 100%;
			position: relative;
		}

		.container {
			height: 100%;
		}
	`;

	@property({type: Object})
	reportContext: ReportContext;

	@query('.container')
	extensionContainer: HTMLElement;

	private _engine: ExtensionEngine;

	invokeAction(id: string, data?: any) {
		this._engine.invokeAction(id, data);
	}

	async download(type: string) {
		if (!this._engine) return;
		// resolve the full export type object declared by the controller
		const exportType = this._engine.getExportTypes().find(t => t.type == type) ?? {type};
		await this._engine.export(exportType);
	}

	getOptions(): VisualizationOptions {
		return this.createOptions({
			actions: this._engine?.getCustomActions(),
			exportTypes: this._engine?.getExportTypes(),
		});
	}

	private static _instanceCounter = 0;
	private readonly _instanceUid = ++ExtensionVisualization._instanceCounter;

	get extensionComponentId() {
		const id = parseComponentId(this.contextData.view);
		const idResource = id.id;
		// the instance uid keeps the id unique per placement - the same extension can be
		// placed multiple times on one dashboard and each placement needs its own component
		return `extensions-view-${idResource}-${this.reportContext?.id_context ?? 0}-i${this._instanceUid}`;
	}

	private _component: HTMLElement;
	private _componentPromise: Promise<HTMLElement>;
	private _registeredComponentId: string;

	getComponent() {
		return this._componentPromise ??= this._createComponent();
	}

	private async _createComponent() {
		this._registeredComponentId = this.extensionComponentId;
		this._component = await rp.host.getComponent(this._registeredComponentId);

		this._component.addEventListener('extension.load', (e: Event & { engine: ExtensionEngine }) => {
			this._engine = e.engine;
			this.dispatchEvent(new ReportEvent('load'));
		});

		this._component.addEventListener('extension.detail', (e: Event & { data: ExtensionDetailData }) => {
			const params: ModifyContextParams = {
				view: `custom-${e.data.idResource}`,
				parameters: e.data.parameters
			};
			openDrillDialog(getDrillContainerId(this.reportContext, params), e.data.caption);
		});

		if (this._component) (this._component as any).reportContext = this.reportContext;
		return this._component;
	}

	async updated(props: Map<string, any>) {
		if (props.has('reportContext')) {
			this.contextData = this.reportContext?.context as ContextData;
			this._disposeComponent();
		}

		const component = await this.getComponent();
		if (component && component.parentElement !== this.extensionContainer) {
			dom.empty(this.extensionContainer);
			this.extensionContainer.appendChild(component);
		}
	}

	private _disposeTimer: ReturnType<typeof setTimeout>;

	/** Grace period before a detached visualization releases its hosted component. */
	private static readonly DISPOSE_GRACE_MS = 5 * 60_000;

	connectedCallback() {
		super.connectedCallback();
		if (this._disposeTimer) {
			clearTimeout(this._disposeTimer);
			this._disposeTimer = null;
		}
		// the component may have been disposed during a long detach - recreate it
		if (!this._componentPromise) this.requestUpdate();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this._disposeTimer = setTimeout(() => {
			if (!this.isConnected) this._disposeComponent();
		}, ExtensionVisualization.DISPOSE_GRACE_MS);
	}

	private _disposeComponent() {
		if (this._registeredComponentId) {
			rp.host.bucket.components.unregister(this._registeredComponentId);
			this._registeredComponentId = null;
		}
		this._component = null;
		this._componentPromise = null;
		this._engine = null;
	}

	render = () => html`<div class="container"></div>`;

	layout() {
		if ((this._component as Layout)?.layout) (this._component as Layout).layout();
	}

}
